import moment from 'moment';
import { G2S } from '../g2s';
import * as flights from '../flights';
import * as api from '../api';
import * as utils from '../utils';

export class FlightSearchService {
    private searchHistoryKey = 'g2sH';
    private searchHistoryMaxItems = 5;

    addHistoryItem(searchParams: flights.FlightSearchParams) {
        if (!searchParams) {
            return;
        }

        let url = new flights.FlightNavigationService().getSearchResultsUrl(searchParams);

        // Store only url params
        url = url.replace('/flights', '');

        new api.DestinationService().list([searchParams.from, searchParams.to]).then(
            (result: api.Destination[]) => {
                // 2 destinations required
                if (result.length === 2) {
                    let items = this.loadHistoryItems();

                    items = items.filter((i) => {
                        return url !== i.url;
                    });

                    // new item
                    const item: flights.SearchHistoryItem = {
                        from: result[0].localizedName,
                        to: result[1].localizedName,
                        departure: searchParams.departure!,
                        return: searchParams.roundtrip ? searchParams.return : null,
                        passengers: searchParams.passengers.adults + searchParams.passengers.children + searchParams.passengers.infants,
                        class: searchParams.class,
                        url: url,
                        timestamp: new Date().getTime(),
                        expires: moment(searchParams.departure!).add(1, 'day').startOf('day').toDate().getTime()
                    };
                    items.unshift(item);

                    // save history
                    this.saveHistoryItems(items);
                }
            }
        );
    }

    public deleteAllHistoryItems() {
        try {
            if (window.localStorage) {
                window.localStorage[this.searchHistoryKey] = '[]';
            }
        }
        catch (err) {
            console.error(err);
        }
    }

    /**
     * Gets all history items from localStorage.
     */
    getHistoryItems(): flights.SearchHistoryItem[] {
        const items = this.loadHistoryItems();

        // Save updated items without expired
        this.saveHistoryItems(items);

        return items;
    }

    /**
     * Loads history items from localStorage and removes expired items.
     */
    private loadHistoryItems(): flights.SearchHistoryItem[] {
        let items: flights.SearchHistoryItem[];

        try {
            items = <flights.SearchHistoryItem[]>JSON.parse(window.localStorage[this.searchHistoryKey]);
        } catch (e) {
            return [];
        }

        if (Object.prototype.toString.call(items) !== '[object Array]') {
            return [];
        }

        // Cleanup old-school history
        if (items && items.length > 0 && !items[0].class) {
            window.localStorage[this.searchHistoryKey] = '[]';
            return [];
        }

        const now = new Date().getTime();

        items = items.filter((i) => {
            return i.expires > now;
        });

        items = items.slice(0, this.searchHistoryMaxItems);

        // Fix old enum booking classes
        items.forEach((item) => {
            if (item.class == '0') {
                item.class = 'Business';
            } else if (item.class == '1') {
                item.class = 'Economic';
            } else if (item.class == '2') {
                item.class = 'EconomicPremium';
            } else if (item.class == '3') {
                item.class = 'EconomicTourist';
            } else if (item.class == '4') {
                item.class = 'First';
            }
        });

        return items;
    }

    /**
     * Saves history items.
     */
    private saveHistoryItems(items: flights.SearchHistoryItem[]) {
        try {
            if (window.localStorage) {
                if (Object.prototype.toString.call(items) !== '[object Array]') {
                    window.localStorage[this.searchHistoryKey] = '[]';
                    return [];
                }

                const now = new Date().getTime();

                items = items.filter((i) => {
                    return i.expires > now;
                });

                items = items.slice(0, this.searchHistoryMaxItems);

                window.localStorage[this.searchHistoryKey] = JSON.stringify(items);
            }
        }
        catch(err) {
            console.error(err);
        }
    }

    /**
     * Searches flights.
     */
    search(request: flights.FlightSearchParams): Promise<flights.FlightSearchResult> {
        const searchRequest: api.FlightSearchRequest = {
            sections: [
                {
                    from: request.from,
                    to: request.to,
                    date: moment(request.departure!).format('YYYY-MM-DDT00:00:00')
                }
            ],
            adults: request.passengers.adults,
            children: request.passengers.children,
            babies: request.passengers.infants,
            bookingClass: request.class,
            isRangeSearch: false,
            partnerCookie: utils.Cookies.getPartnerCookie()
        };

        if (request.roundtrip) {
            searchRequest.sections.push({
                from: request.to,
                to: request.from,
                date: moment(request.return!).format('YYYY-MM-DDT00:00:00')
            });
        }

        const searchStarted = moment();

        return new Promise<flights.FlightSearchResult>((resolve, reject) => {
            new api.FlightService().search(searchRequest)
                .then((response: api.FlightSearchResult) => {
                    const searchResult = this.processResults(request, response);

                    searchResult.duration = moment().diff(searchStarted);

                    resolve(searchResult);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    /**
     * Searches flights.
     */
    processResults(searchParams: flights.FlightSearchParams, response: api.FlightSearchResult): flights.FlightSearchResult {
        const searchResult: {
            searchId: string;
            flights: flights.FlightModel[];
            flightsToFilter: {
                uniqueId: string;
                visible: boolean;
                parentId: string;
                childCount: number;
            }[];
            banners: api.Banner[];
            duration: number;
        } = {
            searchId: response.searchId,
            flights: [],
            flightsToFilter: [],
            banners: response.banners,
            duration: 0
        };

        const flightNavigationService: flights.FlightNavigationService = new flights.FlightNavigationService();

        response.flights.forEach(function (f) {
            const flightModel = new flights.FlightModel(f, response);

            // #4908
            if (flightModel.priority > 0
                && flightModel.airlinesCode.includes('SU')) {
                const u = '/flights/search/results/priority/' + flightModel.airlinesCode;

                if (window['ym']
                    && window['__ymCounterIdBanners']) {
                    window['ym'](window['__ymCounterIdBanners'], 'hit', u);
                }

                if (G2S.isDev) {
                    console.log('YM hit ' + u);
                }
            }

            flightModel.url = flightNavigationService.getBookingUrl(searchParams, response.searchId, f.uniqueId);

            searchResult.flights.push(flightModel);

            searchResult.flightsToFilter.push({
                uniqueId: f.uniqueId,
                visible: true,
                parentId: '',
                childCount: 0
            });
        });

        return searchResult;
    }
}
